import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { ORDER_EMAIL, sendToBeat } from '../../lib/notify';
import type { Product } from '../../types/commerce';
type Status = 'idle' | 'loading' | 'success' | 'error';
interface RestockNotifyProps {
  product: Product;
  /** 'card' sits inside a product card, 'panel' sits in the product page buy box. */
  layout?: 'card' | 'panel';
  className?: string;
}
export function RestockNotify({
  product,
  layout = 'card',
  className = ''
}: RestockNotifyProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const label = `${product.name} ${product.packLabel}`;
  const soldOut = product.badge !== 'Coming soon';
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email) return;
    setStatus('loading');
    const ok = await sendToBeat({
      subject: `BEAT — ${soldOut ? 'restock' : 'launch'} request: ${label}`,
      email,
      product: label,
      message: `${soldOut ? 'Restock' : 'Launch'} notification request.\n\nProduct: ${label}\nEmail: ${email}`
    }, ORDER_EMAIL);
    setStatus(ok ? 'success' : 'error');
    if (ok) setEmail('');
  };
  if (status === 'success') {
    return <p className={`flex items-center gap-2 text-base font-medium text-beat-red ${className}`} data-id="element-280" data-mp="7otdmte0ckv5">
        <Check size={18} data-id="element-281" data-mp="lfy71dqs8gx1" /> We&apos;ll email you the moment it&apos;s back
      </p>;
  }
  return <form onSubmit={handleSubmit} className={`w-full ${layout === 'panel' ? 'max-w-md' : ''} ${className}`} data-id="element-282" data-mp="hewcha7t8t1e">
      <p className="text-base text-white/55" data-id="element-283" data-mp="vdclx8x24xn8">
        {soldOut ? 'Sold out for now. Leave your email and you get first access to the next drop.' : 'Not out yet. Leave your email and you will be first to know.'}
      </p>
      <div className={`mt-4 flex gap-3 ${layout === 'panel' ? 'flex-col sm:flex-row' : 'flex-col'}`} data-id="element-284" data-mp="2x7v50i648n08">
        <label className="sr-only" htmlFor={`restock-${product.handle}`} data-id="element-285" data-mp="w3j6z6slndl4">
          Email for {label} restock notification
        </label>
        <input id={`restock-${product.handle}`} type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="Your email" disabled={status === 'loading'} className="min-h-[52px] flex-1 rounded-full border border-white/20 bg-transparent px-5 text-base text-white placeholder:text-white/40 focus:border-beat-red focus:outline-none disabled:opacity-50" data-id="element-286" data-mp="113npf2d2iidz" />
        <button type="submit" disabled={status === 'loading'} className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-beat-red px-7 text-base font-medium text-white transition-colors duration-150 hover:bg-white hover:text-beat-black disabled:opacity-50" style={{
        transitionTimingFunction: 'cubic-bezier(0.23,1,0.32,1)'
      }} data-id="element-287" data-mp="1oros3unkf4lh">
          {status === 'loading' ? 'Sending…' : 'Notify me'}
        </button>
      </div>
      {status === 'error' && <p className="mt-3 text-sm text-white/50" data-id="element-288" data-mp="27vs5uzukf9dl">
          Didn&apos;t go through — try again or email {ORDER_EMAIL}.
        </p>}
    </form>;
}